// src/pages/EditContact.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";

interface Contact {
  id?: number; // Optional for new entries; fetched entries have an id.
  name: string;
  location: string;
  affiliation: string;
  contact_information: string;
  created_at?: string;
  country_id?: number;
  hidden?: boolean;
}

const EditContact: React.FC = () => {
  // Get the country ID from route parameters.
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [newContact, setNewContact] = useState<Contact>({
    name: "",
    location: "",
    affiliation: "",
    contact_information: "",
  });
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  // State for deletion confirmation.
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [confirmText, setConfirmText] = useState("");

  // Fetch existing contacts for this country on mount.
  useEffect(() => {
    if (id) {
      fetch(`/api/countries/${id}/contacts`)
        .then((res) => res.json())
        .then((data) => {
          // Filter out hidden contacts
          setContacts(data.filter((contact: Contact) => !contact.hidden));
        })
        .catch((err) => setError("Error fetching contacts"));
    }
  }, [id]);

  // Dismiss the "Updated" popup when any click occurs.
  useEffect(() => {
    if (showPopup) {
      const handleDocumentClick = () => setShowPopup(false);
      document.addEventListener("click", handleDocumentClick);
      return () => document.removeEventListener("click", handleDocumentClick);
    }
  }, [showPopup]);

  // Update a specific contact field in state.
  const handleContactChange = (contactId: number, field: keyof Contact, value: string) => {
    setContacts((prev) =>
      prev.map((contact) =>
        contact.id === contactId ? { ...contact, [field]: value } : contact
      )
    );
  };

  // Update the new contact row.
  const handleNewContactChange = (field: keyof Contact, value: string) => {
    setNewContact({ ...newContact, [field]: value });
  };

  // Save an existing contact via PUT.
  const handleUpdate = async (contact: Contact) => {
    if (!contact.id) return;
    try {
      const response = await fetch(`/api/contacts/${contact.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: contact.name,
          location: contact.location,
          affiliation: contact.affiliation,
          contactInformation: contact.contact_information,
          countryId: Number(id),
          hidden: contact.hidden || false,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error updating contact");
      }
    } catch (err) {
      setError("Error connecting to the server");
    }
  };

  // Save the new contact via POST.
  const handleNewSave = async () => {
    if (newContact.name.trim() === "") return;
    try {
      const response = await fetch("/api/contacts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newContact.name,
          location: newContact.location,
          affiliation: newContact.affiliation,
          contactInformation: newContact.contact_information,
          countryId: Number(id),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error adding contact");
      } else {
        const createdContact = await response.json();
        // Only add if not hidden.
        if (!createdContact.hidden) {
          setContacts((prev) => [createdContact, ...prev]);
        }
        setNewContact({ name: "", location: "", affiliation: "", contact_information: "" });
        setError("");
      }
    } catch (err) {
      setError("Error connecting to the server");
    }
  };

  // "Delete" a contact by setting its hidden flag to true (soft delete).
  const handleDeleteContact = async (contact: Contact) => {
    if (!contact.id) return;
    try {
      const response = await fetch(`/api/contacts/${contact.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: contact.name,
          location: contact.location,
          affiliation: contact.affiliation,
          contactInformation: contact.contact_information,
          countryId: Number(id),
          hidden: true,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error deleting contact");
      } else {
        // Remove the contact from local state after soft deletion.
        setContacts((prev) => prev.filter((c) => c.id !== contact.id));
        setShowDeleteModal(false);
        setContactToDelete(null);
        setConfirmText("");
      }
    } catch (err) {
      setError("Error connecting to the server");
    }
  };

  // Open the delete confirmation modal.
  const openDeleteModal = (contact: Contact) => {
    setContactToDelete(contact);
    setConfirmText("");
    setShowDeleteModal(true);
  };

  // Update all contacts (existing and new) then show a confirmation popup.
  const handleUpdateAll = async () => {
    if (newContact.name.trim() !== "") {
      await handleNewSave();
    }
    for (const contact of contacts) {
      if (contact.name.trim() !== "") {
        await handleUpdate(contact);
      }
    }
    setShowPopup(true);
  };

  // Styles (same as in EditDealer.tsx)
  const containerStyle: React.CSSProperties = {
    backgroundColor: "#F0F5F0",
    minHeight: "100vh",
    padding: "20px",
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "600px",
    margin: "50px auto",
    padding: "30px",
  };

  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px",
  };

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
  };

  const thStyle: React.CSSProperties = {
    textAlign: "left",
    padding: "8px",
    borderBottom: "1px solid #ccc",
    color: "#e41e26",
  };

  const tdStyle: React.CSSProperties = {
    padding: "8px",
    borderBottom: "1px solid #ccc",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "5px",
    boxSizing: "border-box",
  };

  const buttonStyle: React.CSSProperties = {
    padding: "10px 20px",
    backgroundColor: "#e41e26",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    marginRight: "10px",
  };

  const navContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "20px",
  };

  const popupStyle: React.CSSProperties = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#e41e26",
    color: "#fff",
    padding: "15px 30px",
    borderRadius: "4px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
    zIndex: 1000,
  };

  const trashButtonStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };

  const modalContentStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    maxWidth: "400px",
    width: "90%",
    textAlign: "center",
  };

  return (
    <div style={containerStyle}>
      <Navbar />
      {showPopup && (
        <div style={popupStyle} onClick={() => setShowPopup(false)}>
          Updated
        </div>
      )}
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Contacts</h1>
        {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Affiliation</th>
              <th style={thStyle}>Contact Info</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => (
              <tr key={contact.id}>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={contact.name}
                    onChange={(e) => handleContactChange(contact.id!, "name", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={contact.location}
                    onChange={(e) => handleContactChange(contact.id!, "location", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={contact.affiliation}
                    onChange={(e) => handleContactChange(contact.id!, "affiliation", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={contact.contact_information}
                    onChange={(e) =>
                      handleContactChange(contact.id!, "contact_information", e.target.value)
                    }
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <button style={trashButtonStyle} onClick={() => openDeleteModal(contact)}>
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
            {/* New contact entry row */}
            <tr>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newContact.name}
                  onChange={(e) => handleNewContactChange("name", e.target.value)}
                  style={inputStyle}
                  placeholder="New Contact Name"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newContact.location}
                  onChange={(e) => handleNewContactChange("location", e.target.value)}
                  style={inputStyle}
                  placeholder="Location"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newContact.affiliation}
                  onChange={(e) => handleNewContactChange("affiliation", e.target.value)}
                  style={inputStyle}
                  placeholder="Affiliation"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newContact.contact_information}
                  onChange={(e) => handleNewContactChange("contact_information", e.target.value)}
                  style={inputStyle}
                  placeholder="Contact Info"
                />
              </td>
              <td style={tdStyle}></td>
            </tr>
          </tbody>
        </table>
        <div style={navContainerStyle}>
          <button style={buttonStyle} onClick={() => navigate(-1)}>
            Back
          </button>
          <button style={buttonStyle} onClick={handleUpdateAll}>
            Update
          </button>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && contactToDelete && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to delete <strong>{contactToDelete.name}</strong>?<br />
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              style={{ width: "80%", padding: "8px", margin: "10px 0" }}
            />
            <div style={{ marginTop: "10px" }}>
              <button
                style={buttonStyle}
                onClick={() => {
                  if (confirmText === "DELETE") {
                    handleDeleteContact(contactToDelete);
                  } else {
                    alert("Please type DELETE to confirm.");
                  }
                }}
              >
                Submit
              </button>
              <button style={{ ...buttonStyle, marginLeft: "10px" }} onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditContact;
